import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import JsxParser from "react-jsx-parser";
import StaffNav from "./managernav";

function shuffleArray(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export default function CourseViewer({ courseDocName }) {
  const [trainingDocuments, setTrainingDocuments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizData, setQuizData] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [trainingId, setTrainingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [wrongQuestionIds, setWrongQuestionIds] = useState([]);
  const [showContents, setShowContents] = useState(true);
  const [savedProgress, setSavedProgress] = useState(null);

  useEffect(() => {
    const fetchTrainingDocuments = async () => {
      try {
        const response = await fetch(`https://api.insightos.co.uk/api/courseviewer/${courseDocName}`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          const sortedDocs = data.data.sort((a, b) => parseInt(a.Page, 10) - parseInt(b.Page, 10));
          setTrainingId(sortedDocs[0].id);
          setTrainingDocuments(sortedDocs);
          const saved = localStorage.getItem(`course_progress_${courseDocName}`);
          if (saved) {
            const p = parseInt(saved, 10);
            if (p > 1 && p <= sortedDocs.length) setSavedProgress(p);
          }
        } else {
          console.error("Failed to fetch training documents");
        }
      } catch (error) {
        console.error("Error fetching training documents:", error);
      }
    };
    fetchTrainingDocuments();
  }, [courseDocName]);

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullScreen(true);
    } else {
      document.exitFullscreen();
      setIsFullScreen(false);
    }
  };

  const speakContent = () => {
    const text = trainingDocuments[currentPage - 1]?.Content;
    if (!text) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    const voices = window.speechSynthesis.getVoices();
    const britishVoice = voices.find((v) => v.lang === "en-GB");
    if (britishVoice) utterance.voice = britishVoice;
    utterance.lang = "en-GB";
    utterance.rate = 0.85;
    window.speechSynthesis.speak(utterance);
    setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
  };

  const stopSpeaking = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  };

  const nextPage = () => {
    if (currentPage < trainingDocuments.length) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  // Fetch quiz data only when Quiz button is clicked
  const handleShowQuiz = async () => {
    if (!trainingId) {
      console.error("Training ID not set, cannot fetch quiz.");
      return;
    }
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/quiz/${trainingId}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        let filtered = data.data;
        filtered = shuffleArray(filtered);
        const randomizedQuiz = filtered.map((q) => {
          const selections = [
            q.SelectionA,
            q.SelectionB,
            q.SelectionC,
            q.SelectionD,
          ].filter(Boolean);
          const shuffledSelections = shuffleArray(selections);
          return {
            ...q,
            selections: shuffledSelections,
          };
        });
        setQuizData(randomizedQuiz);
        setShowQuiz(true);
        setCurrentQuestionIndex(0);
        setSelectedOptions({});
        setScore(0);
        setShowResult(false);
        setSaveMessage("");
        setWrongQuestionIds([]);
      } else {
        console.error("Failed to fetch quiz data");
      }
    } catch (error) {
      console.error("Error fetching quiz data:", error);
    }
  };

  // Compare selected option with the actual answer value (by value, not by label)
  const handleOptionSelect = (option) => {
    const question = quizData[currentQuestionIndex];
    setSelectedOptions((prev) => ({
      ...prev,
      [currentQuestionIndex]: option,
    }));

    // Normalize both option and answer for comparison (trim, lowercase)
    const normalize = (str) =>
      (str ?? "").toString().trim().toLowerCase();

    if (normalize(option) === normalize(question.Answer)) {
      setScore((prev) => prev + 1);
    }
    if (currentQuestionIndex < quizData.length - 1) {
      setTimeout(() => setCurrentQuestionIndex((prev) => prev + 1), 600);
    } else {
      setTimeout(() => setShowResult(true), 600);
    }
  };

  // Save quiz result to backend when quiz is finished
  useEffect(() => {
    const saveQuizResult = async () => {
      if (!showResult || !quizData.length || !trainingId) return;
      setSaving(true);
      setSaveMessage("");
      // Find wrong question IDs
      const normalize = (str) =>
        (str ?? "").toString().trim().toLowerCase();
      const wrongIds = quizData
        .map((q, idx) =>
          selectedOptions[idx] !== undefined &&
          normalize(selectedOptions[idx]) !== normalize(q.Answer)
            ? q.id
            : null
        )
        .filter((id) => id !== null && id !== undefined);
      setWrongQuestionIds(wrongIds);
      // Use Difficulty from first question, or fallback
      const Difficulty = quizData[0]?.Difficulty || "Unknown";
      try {
        const response = await fetch(
          `https://api.insightos.co.uk/api/savequizresult/${trainingId}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              score,
              wrongQuestionIds: wrongIds,
              amountOfQuestions: quizData.length,
              Difficulty,
            }),
          }
        );
        const data = await response.json();
        if (response.ok) {
          setSaveMessage("Quiz result saved successfully.");
        } else {
          setSaveMessage(data.error || "Failed to save quiz result.");
        }
      } catch (error) {
        setSaveMessage("Error saving quiz result.");
      } finally {
        setSaving(false);
      }
    };
    saveQuizResult();
    // eslint-disable-next-line
  }, [showResult]);

  const handleRestartQuiz = () => {
    setCurrentQuestionIndex(0);
    setSelectedOptions({});
    setScore(0);
    setShowResult(false);
    setSaveMessage("");
    setWrongQuestionIds([]);
    setQuizData((prev) =>
      shuffleArray(prev).map((q) => ({
        ...q,
        selections: shuffleArray(q.selections),
      }))
    );
  };

  useEffect(() => {
    if (!showContents && !showQuiz && courseDocName && currentPage) {
      localStorage.setItem(`course_progress_${courseDocName}`, String(currentPage));
    }
  }, [currentPage, showContents, showQuiz, courseDocName]); // eslint-disable-line

  const optionColors = [
    "bg-blue-100 border-blue-400",
    "bg-green-100 border-green-400",
    "bg-yellow-100 border-yellow-400",
    "bg-pink-100 border-pink-400",
  ];

  // Helper for answer comparison in rendering
  const normalize = (str) => (str ?? "").toString().trim().toLowerCase();

  return (
    <>
      {!isFullScreen && <StaffNav />}
      <div className="relative w-full max-w-4xl mx-auto bg-gray-100 p-2 sm:p-4">
        {/* Mobile-responsive control buttons */}
        <div className="absolute top-2 sm:top-4 right-2 sm:right-4 z-10 flex flex-col sm:flex-row gap-2">
          <button
            onClick={toggleFullScreen}
            className="px-2 sm:px-3 py-2 bg-blue-600 text-white rounded-md shadow-lg text-xs sm:text-sm"
          >
            {isFullScreen ? "Exit Full" : "Full Screen"}
          </button>
        </div>
        <div className="absolute top-2 sm:top-4 left-2 sm:left-4 z-10">
          <button
            onClick={() => (isSpeaking ? stopSpeaking() : speakContent())}
            className="px-2 sm:px-3 py-2 bg-green-600 text-white rounded-md shadow-lg text-xs sm:text-sm"
          >
          {isSpeaking ? "Stop Reading" : "Read Aloud"}
        </button>
        {showContents ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full h-auto min-h-[80vh] bg-white shadow-lg rounded-lg overflow-hidden flex flex-col"
          >
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5 flex-shrink-0">
              <h1 className="text-white text-xl sm:text-2xl font-bold">Course Contents</h1>
              {trainingDocuments.length > 0 && (
                <p className="text-indigo-200 text-sm mt-1">{trainingDocuments.length} pages</p>
              )}
            </div>

            {trainingDocuments.length === 0 ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                {savedProgress && (
                  <div className="mx-4 sm:mx-6 mt-4 flex-shrink-0 p-3 bg-indigo-50 border border-indigo-200 rounded-lg flex items-center justify-between gap-3">
                    <span className="text-indigo-700 text-sm font-medium min-w-0 truncate">
                      📍 Left off at Page {trainingDocuments[savedProgress - 1]?.Page}
                      {trainingDocuments[savedProgress - 1]?.Title ? ` — ${trainingDocuments[savedProgress - 1].Title}` : ''}
                    </span>
                    <button
                      onClick={() => { setCurrentPage(savedProgress); setShowContents(false); }}
                      className="flex-shrink-0 px-4 py-1.5 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 transition"
                    >
                      Continue →
                    </button>
                  </div>
                )}

                <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-2">
                  {trainingDocuments.map((doc, idx) => (
                    <button
                      key={doc.id}
                      onClick={() => { setCurrentPage(idx + 1); setShowContents(false); }}
                      className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:bg-indigo-50 hover:border-indigo-200 transition text-left group"
                    >
                      <span className="text-xs font-bold text-indigo-600 bg-indigo-100 px-2 py-1 rounded-full w-14 text-center flex-shrink-0">
                        P {doc.Page}
                      </span>
                      <span className="flex-1 text-sm font-medium text-gray-800 truncate">{doc.Title || `Page ${doc.Page}`}</span>
                      <span className="text-indigo-400 text-xs opacity-0 group-hover:opacity-100 transition flex-shrink-0">Go →</span>
                    </button>
                  ))}
                </div>

                <div className="px-4 sm:px-6 pb-5 pt-3 border-t border-gray-100 flex-shrink-0">
                  <button
                    onClick={() => { setCurrentPage(1); setShowContents(false); }}
                    className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-lg hover:opacity-90 transition text-sm sm:text-base"
                  >
                    Start from Beginning →
                  </button>
                </div>
              </>
            )}
          </motion.div>
        ) : !showQuiz ? (
          <motion.div
            key={currentPage}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            className="w-full h-auto min-h-[80vh] flex flex-col justify-center bg-white shadow-lg overflow-hidden p-4 sm:p-6 rounded-lg"
          >
            {/* Content Area */}
            <div className="w-full flex-grow mb-6">
              <div className="prose prose-sm sm:prose-base lg:prose-lg max-w-none">
                {trainingDocuments[currentPage - 1] && (
                  <JsxParser components={{}} jsx={trainingDocuments[currentPage - 1].Code} />
                )}
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{width: `${(currentPage / trainingDocuments.length) * 100}%`}}
                ></div>
              </div>
              <p className="text-xs sm:text-sm text-gray-600 mt-2 text-center">
                Page {currentPage} of {trainingDocuments.length}
              </p>
            </div>

            {/* Navigation Buttons */}
            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sm:gap-4">
              <button
                onClick={prevPage}
                disabled={currentPage === 1}
                className="w-full sm:w-auto px-4 sm:px-6 py-3 bg-red-600 text-white rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed font-medium transition-colors duration-200"
              >
                ← Previous
              </button>

              <button
                onClick={() => setShowContents(true)}
                className="w-full sm:w-auto px-4 sm:px-6 py-3 bg-gray-100 text-gray-700 border border-gray-300 rounded-lg font-medium transition-colors duration-200 hover:bg-gray-200"
              >
                ☰ Contents
              </button>
              
              {currentPage === trainingDocuments.length ? (
                <button
                  onClick={handleShowQuiz}
                  data-component ="CourseViewer"
                  data-feature ="StartQuizButton"
                  data-time-saved="3"
                  className="w-full sm:w-auto px-4 sm:px-6 py-3 bg-gradient-to-r from-emerald-400 to-blue-500 animate-pulse text-white rounded-lg font-bold shadow-lg hover:scale-105 transition-transform duration-200"
                >
                  🎯 Start Quiz
                </button>
              ) : (
                <button
                  onClick={nextPage}
                  className="w-full sm:w-auto px-4 sm:px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium transition-colors duration-200 hover:bg-indigo-700"
                  data-component ="CourseViewer"
                  data-feature ="NextPageButton"
                  data-time-saved="1"
                >
                  Next →
                </button>
              )}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key={currentQuestionIndex}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="w-full h-auto min-h-[80vh] flex flex-col justify-center bg-gradient-to-br from-blue-50 via-white to-emerald-50 shadow-lg overflow-hidden p-4 sm:p-6 rounded-lg"
          >
            {quizData && quizData.length > 0 && quizData[currentQuestionIndex] ? (
              showResult ? (
                <div className="text-center px-4">
                  {/* Score Display */}
                  <div className="mb-6">
                    <div className="inline-block px-4 sm:px-6 py-4 bg-gradient-to-r from-green-400 to-blue-500 text-white rounded-lg shadow-lg">
                      <div className="text-lg sm:text-2xl font-bold">
                        🎉 Your Score
                      </div>
                      <div className="text-2xl sm:text-3xl font-bold mt-2">
                        {score} / {quizData.length}
                      </div>
                      <div className="text-sm sm:text-base mt-1">
                        {Math.round((score / quizData.length) * 100)}%
                      </div>
                    </div>
                  </div>

                  {/* Status Messages */}
                  {saving && (
                    <div className="text-blue-600 font-semibold mb-4 text-sm sm:text-base">
                      💾 Saving result...
                    </div>
                  )}
                  {saveMessage && (
                    <div className="text-green-700 font-semibold mb-4 text-sm sm:text-base bg-green-50 p-3 rounded-lg">
                      ✅ {saveMessage}
                    </div>
                  )}

                  {/* Success or Retry Section */}
                  {score === quizData.length ? (
                    <div className="flex flex-col items-center gap-4">
                      <div className="text-green-600 font-bold text-lg sm:text-xl mb-2">
                        🏆 Perfect Score! Well Done!
                      </div>
                      <button
                        onClick={() => window.location.reload()}
                        className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-blue-500 to-green-400 text-white rounded-lg font-semibold shadow-md hover:scale-105 transition-transform duration-200"
                      >
                        📚 Back to Courses
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-4">
                      <div className="text-red-600 font-semibold mb-2 text-sm sm:text-base bg-red-50 p-4 rounded-lg">
                        📝 You must score 100% to complete this quiz. Please review the material and try again.
                      </div>
                      <button
                        onClick={handleRestartQuiz}
                        className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-blue-500 to-green-400 text-white rounded-lg font-semibold shadow-md hover:scale-105 transition-transform duration-200"
                      >
                        🔄 Retry Quiz
                      </button>
                      {wrongQuestionIds.length > 0 && (
                        <div className="mt-2 text-xs sm:text-sm text-gray-600 bg-gray-50 p-3 rounded-lg max-w-full overflow-hidden">
                          <span className="font-medium">Review questions:</span> {wrongQuestionIds.join(", ")}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="px-2">
                  {/* Question Header */}
                  <div className="mb-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
                      <h3 className="font-bold text-lg sm:text-xl text-blue-700 drop-shadow">
                        Question {currentQuestionIndex + 1} of {quizData.length}
                      </h3>
                      <div className="text-xs sm:text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                        {Math.round(((currentQuestionIndex + 1) / quizData.length) * 100)}% Complete
                      </div>
                    </div>
                    
                    {/* Question Text */}
                    <div className="mb-6 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                      <div className="text-base sm:text-lg font-medium text-gray-800 leading-relaxed">
                        {quizData[currentQuestionIndex]?.Question}
                      </div>
                    </div>

                    {/* Answer Options */}
                    <div className="space-y-3">
                      {quizData[currentQuestionIndex].selections.map((option, i) => {
                        const isSelected = selectedOptions[currentQuestionIndex] === option;
                        const isCorrect = normalize(option) === normalize(quizData[currentQuestionIndex].Answer);
                        const isWrong = isSelected && !isCorrect;
                        
                        return (
                          <label
                            key={option}
                            className={`flex items-start sm:items-center p-3 sm:p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                              isSelected
                                ? isCorrect
                                  ? "bg-green-100 border-green-500 shadow-md"
                                  : isWrong
                                  ? "bg-red-100 border-red-500 shadow-md"
                                  : optionColors[i % optionColors.length]
                                : optionColors[i % optionColors.length] + " hover:shadow-md"
                            } ${
                              selectedOptions[currentQuestionIndex] !== undefined
                                ? "opacity-90"
                                : "hover:scale-[1.02]"
                            }`}
                          >
                            <input
                              type="radio"
                              name={`question-${currentQuestionIndex}`}
                              value={option}
                              checked={isSelected}
                              onChange={() => handleOptionSelect(option)}
                              className="mr-3 mt-1 sm:mt-0 accent-blue-600 flex-shrink-0"
                              disabled={selectedOptions[currentQuestionIndex] !== undefined}
                            />
                            <div className="flex-1 min-w-0">
                              <span className="font-medium text-sm sm:text-base break-words">
                                {option}
                              </span>
                              {isCorrect && isSelected && (
                                <div className="mt-1 text-green-700 font-bold text-xs sm:text-sm">
                                  ✅ Correct Answer!
                                </div>
                              )}
                              {isWrong && (
                                <div className="mt-1 text-red-700 font-bold text-xs sm:text-sm">
                                  ❌ Incorrect
                                </div>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Navigation Buttons */}
                  <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sm:gap-4 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => setCurrentQuestionIndex((prev) => Math.max(prev - 1, 0))}
                      disabled={currentQuestionIndex === 0}
                      className="w-full sm:w-auto px-4 sm:px-6 py-3 bg-red-500 text-white rounded-lg font-semibold shadow disabled:bg-gray-400 disabled:cursor-not-allowed hover:bg-red-600 transition-colors duration-200"
                    >
                      ← Previous Question
                    </button>
                    
                    <div className="text-center text-sm text-gray-500">
                      Question {currentQuestionIndex + 1} / {quizData.length}
                    </div>
                    
                    <button
                      onClick={() =>
                        setCurrentQuestionIndex((prev) =>
                          Math.min(prev + 1, quizData.length - 1)
                        )
                      }
                      disabled={currentQuestionIndex >= quizData.length - 1 || showResult}
                      className="w-full sm:w-auto px-4 sm:px-6 py-3 bg-indigo-500 text-white rounded-lg font-semibold shadow disabled:bg-gray-400 disabled:cursor-not-allowed hover:bg-indigo-600 transition-colors duration-200"
                    >
                      Next Question →
                    </button>
                  </div>
                </div>
              )
            ) : (
              <div className="text-center text-lg text-gray-500">No quiz data available.</div>
            )}
          </motion.div>
        )}
      </div>
    </div>
    </>
  );
}
